import React, { useState } from 'react';
import { Navigate, Route, Routes, NavLink } from 'react-router-dom';
import { LayoutDashboard, ReceiptText, Users, LogOut, Building2, BookOpen, GraduationCap } from 'lucide-react';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PaymentList from './components/PaymentList';
import RegistrationList from './components/RegistrationList';
import PaymentDetail from './components/PaymentDetail';
import CenterList from './components/CenterList';
import CourseList from './components/CourseList';
import StudentRoster from './components/StudentRoster';

function NavItem({ to, icon: Icon, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
          isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span>{children}</span>
    </NavLink>
  );
}

function DashboardShell({ onSignOut }) {
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [paymentRefreshToken, setPaymentRefreshToken] = useState(0);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-blue-600">School Payment Bot</p>
            <h1 className="text-2xl font-bold">Admin Console</h1>
          </div>
          <button
            onClick={onSignOut}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[280px_1fr] lg:px-8">
        <aside className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm h-fit">
          <nav className="space-y-2">
            <NavItem to="/dashboard" icon={LayoutDashboard}>Dashboard</NavItem>
            <NavItem to="/dashboard/centers" icon={Building2}>Centers</NavItem>
            <NavItem to="/dashboard/courses" icon={BookOpen}>Courses</NavItem>
            <NavItem to="/dashboard/registrations" icon={Users}>Registrations</NavItem>
            <NavItem to="/dashboard/students" icon={GraduationCap}>Students</NavItem>
            <NavItem to="/dashboard/payments" icon={ReceiptText}>Payments</NavItem>
          </nav>
          <p className="text-sm text-slate-500">
            Live data from the Telegram bot backend: parent registrations and payment screenshots.
          </p>
        </aside>

        <section className="space-y-6">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/centers" element={<CenterList />} />
            <Route path="/courses" element={<CourseList />} />
            <Route path="/registrations" element={<RegistrationList />} />
            <Route path="/students" element={<StudentRoster />} />
            <Route
              path="/payments"
              element={
                <PaymentList
                  onSelectPayment={setSelectedPaymentId}
                  refreshToken={paymentRefreshToken}
                />
              }
            />
          </Routes>

          {selectedPaymentId && (
            <PaymentDetail
              paymentId={selectedPaymentId}
              onClose={() => setSelectedPaymentId(null)}
              onStatusChange={() => setPaymentRefreshToken((value) => value + 1)}
            />
          )}
        </section>
      </main>
    </div>
  );
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(localStorage.getItem('auth_token')));

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleSignOut = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_role');
    setIsAuthenticated(false);
  };

  return (
    <Routes>
      <Route path="/" element={<Login onLoginSuccess={handleLoginSuccess} />} />
      <Route
        path="/dashboard/*"
        element={isAuthenticated ? <DashboardShell onSignOut={handleSignOut} /> : <Navigate to="/" replace />}
      />
      <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />} />
    </Routes>
  );
}
